import React, { useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectOrganizationsData } from '../../../store/organization/organization-slice-selectors';
import { DeleteIcon, RobotIcon, TransferIcon } from '@common/utils/icons/icons';
import TextInput from '@common/components/Inputs/TextInput';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import OrganizationRole from '../../../models/enums/organization-roles';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import {
  selectIsUserAdminOrHigher,
  selectUser,
} from '../../../store/general/general-slice-selectors';
import { selectRobotsData } from '../../../store/robots/robots-slice-selectors';
import { resetRobotsRestState } from '../../../store/robots/robots-slice';
import {
  validateRobotName,
  validateRobotSerialNumber,
} from '../../../services/validation/robot-validation';
import { deleteRobotById, patchRobotById } from '../../../store/robots/robots-actions';
import SearchResult from '../../../models/misc/search-result';
import SearchType from '../../../models/enums/search';
import SearchBar from '../../ui/SearchBar';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { replaceArgsInMessage } from '../../../utils/strings';
import CustomConfirmAbortDialog from '../../misc/CustomConfirmAbortDialog';
import Button from '@mui/material/Button';
import { isHttpError } from '@common/interfaces/api/http-error';
import Tooltip from '@mui/material/Tooltip';
import { useSearchParams } from 'react-router-dom';
import { FilterKeys } from '@common/enums/query-params/filters';
import VersionSettings from './VersionSettings';

const GeneralRobotSettings: React.FC = () => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setFilterParams] = useSearchParams();

  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const user = useAppSelector((state) => selectUser(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));
  const organizationsData = useAppSelector((state) => selectOrganizationsData(state));

  const { resetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      patchError: robotsData.rest.patchError,
      resetPatchHandler: () =>
        dispatch(resetRobotsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
    });
  const [selectedOrganization, setSelectedOrganization] = useState<SearchResult | null>(null);
  const [isConfirmDeleteRobotOpen, setIsConfirmDeleteRobotOpen] = useState(false);

  const robot = robotsData.currentRobot;

  const {
    value: name,
    validationResult: nameValidationResult,
    valueChangeHandler: nameChangeHandler,
  } = useValidatedInputEffect(
    robot?.name ?? '',
    (newValue) => validateRobotName(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('name'),
    removeServerError,
    resetToggle,
  );

  const {
    value: serialNumber,
    validationResult: serialNumberValidationResult,
    valueChangeHandler: serialNumberChangeHandler,
  } = useValidatedInputEffect(
    robot?.serialNumber ?? '',
    (newValue) => validateRobotSerialNumber(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('serialNumber'),
    removeServerError,
    resetToggle,
  );

  const patchRobotHandler = () => {
    if (robot?.id != null) {
      dispatch(
        patchRobotById({
          id: robot.id,
          data: {
            name,
            serialNumber,
            organizationId: selectedOrganization?.id ?? undefined,
          },
        }),
      );
    }
    setSelectedOrganization(null);
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    patchRobotHandler();
  };

  const closeSnackbarHandler = () => {
    dispatch(resetRobotsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
  };

  const selectOrganizationClickHandler = (result: SearchResult) => {
    setSelectedOrganization(result);
  };

  const closeSearchHandler = () => {
    setSelectedOrganization(null);
  };

  const openConfirmDeleteClickHandler = () => {
    setIsConfirmDeleteRobotOpen(true);
  };

  const closeDeleteRobotHandler = () => {
    setIsConfirmDeleteRobotOpen(false);
    dispatch(resetRobotsRestState({ deleteError: null, deleteStatus: HttpFetchStatus.Idle }));
  };

  const confirmDeleteRobotHandler = async () => {
    if (robot == null) return;

    const result = await dispatch(deleteRobotById(robot.id));

    if (!deleteRobotById.fulfilled.match(result)) return;

    // Closing the robot overlay in the same update as the delete state reset keeps the robot page
    // from refetching the deleted robot (details / local data state) before it unmounts.
    setFilterParams((prevState) => {
      prevState.delete(FilterKeys.RobotId);
      prevState.delete(FilterKeys.Tab);
      return prevState;
    });
  };

  return (
    <React.Fragment>
      <Stack p={0.3} spacing={2} alignItems="center">
        <CustomSplitColumnCard
          title={t('title-general-settings')}
          content={[
            {
              left: (
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <RobotIcon />
                  <Typography>{t('title-robot-general-settings')}</Typography>
                </Stack>
              ),
              right: (
                <React.Fragment>
                  <form onSubmit={formSubmitHandler}>
                    <Stack py={2} spacing={2}>
                      <TextInput
                        onBlur={patchRobotHandler}
                        adminOnly
                        value={name}
                        onChange={nameChangeHandler}
                        validationResult={nameValidationResult}
                        label={t('input-label-robot-name')}
                      />
                      {user.data.organization.role === OrganizationRole.MANUFACTURER && (
                        <TextInput
                          onBlur={patchRobotHandler}
                          adminOnly
                          value={serialNumber}
                          onChange={serialNumberChangeHandler}
                          validationResult={serialNumberValidationResult}
                          label={t('input-label-robot-serial-number')}
                        />
                      )}
                    </Stack>
                    <Button sx={{ display: 'none' }} type="submit" />
                  </form>
                  {isAdmin && user.data.organization.role === OrganizationRole.MANUFACTURER && (
                    <Tooltip
                      title={
                        robot?.isDeletable
                          ? undefined
                          : t('info-tooltip-cannot-delete-robot-with-missions')
                      }
                    >
                      <span>
                        <Button
                          color="error"
                          variant="outlined"
                          fullWidth
                          size="large"
                          disabled={
                            robot?.isDeletable !== true ||
                            robotsData.rest.deleteStatus === HttpFetchStatus.Loading
                          }
                          startIcon={<DeleteIcon data-testid="delete-robot-button" />}
                          onClick={openConfirmDeleteClickHandler}
                        >
                          {t('button-delete-robot')}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </React.Fragment>
              ),
            },
            isAdmin
              ? {
                  left: (
                    <Stack spacing={2} pr={2}>
                      <Stack direction="row" alignItems="center" spacing={1} py={1}>
                        <TransferIcon />
                        <Typography>{t('title-transfer-robot')}</Typography>
                      </Stack>
                      <CustomAlert
                        severity="info"
                        header={replaceArgsInMessage(t('info-transfer-from'), [
                          robot?.organizationName ?? '',
                        ])}
                      />
                      <Typography>{t('info-transfer')}</Typography>
                    </Stack>
                  ),
                  right: (
                    <Stack py={2}>
                      {robot != null && (
                        <SearchBar
                          hideTitle
                          topLabel={t('input-label-transfer-to')}
                          suggestions={organizationsData.locationSet
                            ?.filter((org) => org.id !== robot.organizationId)
                            .map((org) => ({
                              id: org.id,
                              name: org.name,
                              type: SearchType.ORGANIZATION,
                              role: org.role,
                            }))}
                          onItemClick={selectOrganizationClickHandler}
                          initialValue={selectedOrganization?.name}
                          onClose={closeSearchHandler}
                          fields={[SearchType.ORGANIZATION]}
                          fullSize
                          inline
                        />
                      )}
                    </Stack>
                  ),
                }
              : null,
          ]}
        />

        <VersionSettings />
      </Stack>
      <CustomSnackbar
        open={robotsData.rest.patchStatus === HttpFetchStatus.Success}
        title={t('success-patching-robot')}
        onClose={closeSnackbarHandler}
      />
      <CustomConfirmAbortDialog
        open={selectedOrganization != null}
        title={t('title-transfer-robot')}
        text={
          <Stack spacing={2}>
            <CustomAlert
              severity="info"
              header={replaceArgsInMessage(t('info-confirm-transfer-popup-header'), [
                selectedOrganization?.name ?? '',
              ])}
            />
            <Typography variant="body2">{t('info-transfer')}</Typography>
          </Stack>
        }
        confirmButton={t('button-confirm')}
        abortButton={t('button-cancel')}
        onAbort={closeSearchHandler}
        onConfirm={patchRobotHandler}
      />
      <CustomConfirmAbortDialog
        open={isConfirmDeleteRobotOpen}
        title={t('title-confirm-delete-robot')}
        text={
          <React.Fragment>
            {robotsData.rest.deleteError != null && (
              <CustomAlert
                header={
                  isHttpError(robotsData.rest.deleteError)
                    ? robotsData.rest.deleteError.message
                    : undefined
                }
                textMessages={
                  isHttpError(robotsData.rest.deleteError)
                    ? undefined
                    : robotsData.rest.deleteError
                        .filter((error) => error.error != null)
                        .map((error) => t(error.error!))
                }
                severity="error"
              />
            )}
            <Typography>{t('info-confirm-delete-robot')}</Typography>
          </React.Fragment>
        }
        confirmButton={t('button-confirm')}
        abortButton={t('button-cancel')}
        onAbort={closeDeleteRobotHandler}
        onConfirm={confirmDeleteRobotHandler}
      />
    </React.Fragment>
  );
};

export default GeneralRobotSettings;
